import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardBody, Button, Spinner, Alert, Row, Col, Badge } from 'reactstrap';
import { toast } from 'react-hot-toast';
import CryptoJS from 'crypto-js';
import { getToken } from '../../../utility/AuthService';
import { useSelector } from 'react-redux';
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

const ProgressAssessmentReports = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progressAssessments, setProgressAssessments] = useState([]);
    const [completedAssessments, setCompletedAssessments] = useState([]);
    const [inProgressAssessments, setInProgressAssessments] = useState([]);
    const authToken = getToken();
    const languageUse = useSelector(selectLanguageUse);

    const loadProgressAssessments = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Import the service dynamically to avoid circular dependencies
            const { getProgressAssesmentListService } = await import('../../../services/submissionService');
            
            const result = await getProgressAssesmentListService({ authToken });
            
            if (result?.data?.statusCode === 200) {
                const assessments = result.data.data.list || [];
                setProgressAssessments(assessments);
                
                // Separate assessments by status
                const completed = assessments.filter(assessment => !assessment.isDraft);
                const inProgress = assessments.filter(assessment => assessment.isDraft);
                
                setCompletedAssessments(completed);
                setInProgressAssessments(inProgress);
            } else {
                throw new Error(result?.data?.message || 'Failed to load progress assessments');
            }
        } catch (error) {
            console.error('Error loading progress assessments:', error);
            setError(
                languageUse === 'English'
                    ? `Failed to load reports: ${error.message}`
                    : `Échec du chargement des rapports: ${error.message}`
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadProgressAssessments();
    }, []);

    const handleViewReport = (assessmentId) => {
        const encryptedId = CryptoJS.AES.encrypt(assessmentId, 'mySecretKey').toString();
        navigate(`/user/progress/dashboard/${encodeURIComponent(encryptedId)}`);
    };

    const handleStartNewAssessment = () => {
        navigate('/progress-assessment/new');
    };

    const handleBackToHistory = () => {
        navigate('/user/history');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    if (isLoading) {
        return (
            <div className="container mt-4 text-center">
                <Spinner size="lg" color="primary" />
                <p className="mt-3 text-muted">
                    {languageUse === 'English' ? 'Loading reports...' : 'Chargement des rapports...'}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <Alert color="danger">
                            <h5>
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {languageUse === 'English' ? 'Error' : 'Erreur'}
                            </h5>
                            <p>{error}</p>
                            <Button color="outline-secondary" onClick={handleBackToHistory}>
                                <i className="bi bi-arrow-left me-2"></i>
                                {languageUse === 'English' ? 'Back to History' : 'Retour à l\'historique'}
                            </Button>
                        </Alert>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <CardHeader className="bg-success text-white">
                            <CardTitle tag="h4" className="mb-0">
                                <i className="bi bi-graph-up me-2"></i>
                                {languageUse === 'English' ? 'Progress Assessment Reports' : 'Rapports d\'évaluation des progrès'}
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="p-4">
                            {/* Summary Statistics */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Card className="text-center bg-light">
                                        <CardBody>
                                            <h3 className="text-primary">{progressAssessments.length}</h3>
                                            <p className="mb-0 text-muted">
                                                {languageUse === 'English' ? 'Total Assessments' : 'Total des évaluations'}
                                            </p>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="text-center bg-light">
                                        <CardBody>
                                            <h3 className="text-success">{completedAssessments.length}</h3>
                                            <p className="mb-0 text-muted">
                                                {languageUse === 'English' ? 'Completed' : 'Terminées'}
                                            </p>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="text-center bg-light">
                                        <CardBody>
                                            <h3 className="text-warning">{inProgressAssessments.length}</h3>
                                            <p className="mb-0 text-muted">
                                                {languageUse === 'English' ? 'In Progress' : 'En cours'}
                                            </p>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Action Buttons */}
                            <div className="d-flex gap-2 mb-4">
                                <Button 
                                    color="primary" 
                                    onClick={handleStartNewAssessment}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    {languageUse === 'English' ? 'Start New Assessment' : 'Commencer une nouvelle évaluation'}
                                </Button>
                                
                                <Button 
                                    color="outline-secondary" 
                                    onClick={handleBackToHistory}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    {languageUse === 'English' ? 'Back to History' : 'Retour à l\'historique'}
                                </Button>
                            </div>

                            {/* Completed Assessments */}
                            {completedAssessments.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="mb-3">
                                        <i className="bi bi-check-circle text-success me-2"></i>
                                        {languageUse === 'English' ? 'Completed Assessments' : 'Évaluations terminées'}
                                    </h5>
                                    <Row>
                                        {completedAssessments.map((assessment, index) => (
                                            <Col md={6} lg={4} key={assessment._id} className="mb-3">
                                                <Card className="h-100">
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <h6 className="mb-0">
                                                                {languageUse === 'English' ? 'Progress Assessment' : 'Évaluation des progrès'} #{index + 1}
                                                            </h6>
                                                            <Badge color="success">
                                                                {languageUse === 'English' ? 'Completed' : 'Terminé'}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-muted small mb-2">
                                                            <strong>
                                                                {languageUse === 'English' ? 'Created:' : 'Créé:'}
                                                            </strong> {formatDate(assessment.createdAt)}
                                                        </p>
                                                        <p className="text-muted small mb-3">
                                                            <strong>
                                                                {languageUse === 'English' ? 'Completed:' : 'Terminé:'}
                                                            </strong> {formatDate(assessment.updatedAt)}
                                                        </p>
                                                        <Button 
                                                            color="outline-primary" 
                                                            size="sm" 
                                                            block
                                                            onClick={() => handleViewReport(assessment._id)}
                                                        >
                                                            <i className="bi bi-eye me-2"></i>
                                                            {languageUse === 'English' ? 'View Report' : 'Voir le rapport'}
                                                        </Button>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}

                            {/* In Progress Assessments */}
                            {inProgressAssessments.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="mb-3">
                                        <i className="bi bi-clock text-warning me-2"></i>
                                        {languageUse === 'English' ? 'In Progress Assessments' : 'Évaluations en cours'}
                                    </h5>
                                    <Row>
                                        {inProgressAssessments.map((assessment, index) => (
                                            <Col md={6} lg={4} key={assessment._id} className="mb-3">
                                                <Card className="h-100">
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <h6 className="mb-0">
                                                                {languageUse === 'English' ? 'Progress Assessment' : 'Évaluation des progrès'} #{index + 1}
                                                            </h6>
                                                            <Badge color="warning">
                                                                {languageUse === 'English' ? 'In Progress' : 'En cours'}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-muted small mb-2">
                                                            <strong>
                                                                {languageUse === 'English' ? 'Created:' : 'Créé:'}
                                                            </strong> {formatDate(assessment.createdAt)}
                                                        </p>
                                                        <p className="text-muted small mb-3">
                                                            <strong>
                                                                {languageUse === 'English' ? 'Last Updated:' : 'Dernière mise à jour:'}
                                                            </strong> {formatDate(assessment.updatedAt)}
                                                        </p>
                                                        <Button 
                                                            color="outline-warning" 
                                                            size="sm" 
                                                            block
                                                            onClick={() => handleViewReport(assessment._id)}
                                                        >
                                                            <i className="bi bi-play-circle me-2"></i>
                                                            {languageUse === 'English' ? 'Continue' : 'Continuer'}
                                                        </Button>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}

                            {/* No Assessments Message */}
                            {progressAssessments.length === 0 && (
                                <div className="text-center py-5">
                                    <div className="mb-3">
                                        <i className="bi bi-clipboard-data" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                    </div>
                                    <h5 className="text-muted">
                                        {languageUse === 'English' 
                                            ? 'No Progress Assessments Yet' 
                                            : 'Aucune évaluation des progrès pour le moment'}
                                    </h5>
                                    <p className="text-muted">
                                        {languageUse === 'English'
                                            ? 'Start your first Progress Assessment to begin tracking your sustainability improvements.'
                                            : 'Commencez votre première évaluation des progrès pour commencer à suivre vos améliorations en matière de durabilité.'}
                                    </p>
                                    <Button 
                                        color="primary" 
                                        size="lg"
                                        onClick={handleStartNewAssessment}
                                    >
                                        <i className="bi bi-play-circle me-2"></i>
                                        {languageUse === 'English' ? 'Start First Assessment' : 'Commencer la première évaluation'}
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ProgressAssessmentReports;
